import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, InputLabel, Skeleton, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import dayjs from "dayjs";
import InlineDropdown from "../../common/InlineDropdown/InlineDropdown";
import useStyles from "./NIISensitivityModelerGlobal.styles";
import { useHedgeRatioContext } from "../../../context/HedgeRatioContext";
import { useUserContext } from "../../../context/UserContext";
import NiiInputTable from "./tables/NiiInputTable";
import NiiOutputTable from "./tables/NiiOutputTable";
import NiiResultTable from "./tables/NiiResultTable";
import {
  UP100,
  LATEST_ANNUAL_NII_FORECAST_ACCOUNT_NAME,
  MEASURE,
  DIMENSION,
} from "../../../utils/constants";
import ProgressSteps from "../../common/Steps/ProgressSteps";
import { useApprovalContext } from "../../../context/ApprovalContext";
import NIISensitivityModelerGlobalExport from "./NIISensitivityModelerGlobalExport";

const flattenReportData = (
  nestedData,
  selectedFigureDivisor,
  selectedDecimalsIn,
) => {
  if (!Array.isArray(nestedData)) return [];
  const divisor = parseFloat(selectedFigureDivisor) || 1;
  const shouldDivide = divisor !== 1;
  const decimals =
    selectedDecimalsIn !== null && selectedDecimalsIn !== undefined
      ? parseInt(selectedDecimalsIn, 10)
      : null;
  const shouldFormatDecimals = decimals !== null && !isNaN(decimals);

  const processNode = (node) => {
    const formattedDatas = {};
    if (node.datas) {
      Object.keys(node.datas).forEach((key) => {
        let value = node.datas[key];
        if (shouldDivide) {
          value = value / divisor;
        }
        formattedDatas[key] = shouldFormatDecimals
          ? Number(value.toFixed(decimals))
          : value;
      });
    }
    return {
      ...node,
      datas: formattedDatas,
      subAccounts: Array.isArray(node.subAccounts)
        ? node.subAccounts.map(processNode)
        : [],
    };
  };

  return nestedData.map(processNode);
};

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const NIISensitivityModelerGlobalReports = ({
  toSave = false,
  toCompare = false,
  isWritableSfc = false,
  appliedFilters = null,
} = {}) => {
  const { classes } = useStyles(toCompare);

  const [niiInputTablecolumn, setNiiInputTablecolumn] = useState([]);
  const [niiInputTableData, setNiiInputTableData] = useState([]);
  const [niiOutputTablecolumn, setNiiOutputTablecolumn] = useState([]);
  const [niiOutputTableData, setNiiOutputTableData] = useState([]);
  const [niiResultTableColumn, setNiiResultTableColumn] = useState([]);
  const [niiResultTableData, setNiiResultTableData] = useState([]);

  const { selectedUserDetailLoading, selectedUserDetail, isAdmin } =
    useUserContext();

  const {
    hrFiltersDataLoading,
    hrGapResultDataLoading,
    hrGapResultDataError,
    hrGapResultData,
    setHrGapResultData,
    selectedBusinessEntityID,
    localCurrency,
    selectedModelDatabase,
    figureDivisors,
    selectedFigureDivisor,
    setSelectedFigureDivisor,
    decimalsIn,
    selectedDecimalsIn,
    setSelectedDecimalsIn,
    selectedMonthEnd,
    fetchHrGapResultReport,
  } = useHedgeRatioContext();

  const {
    fetchApprovalFlowData,
    approvalFlow,
    setApprovalFlow,
    saveApprovalData,
    sendApproverEmailNotifyForSelectedReport,
    sendEmailNotifyLoading,
  } = useApprovalContext();

  const { email } = selectedUserDetail || {};

  const APPROVAL_FLOW_PARAMS = useMemo(() => {
    return {
      region: null,
      businessEntityID: selectedBusinessEntityID,
      selectedReportDate: selectedMonthEnd,
      isGlobal: true,
      isModelerReport: true,
      isTreasuryKRIReport: false,
      isKRISummaryReport: false,
      isSFCKRIGlobalSummaryReport: false,
      isRegionKRIGlobalSummaryReport: false,
      isTFSCKRIGlobalSummaryReport: false,
      measure: MEASURE.HUNDRED_BP_PARALLEL_SHIFT_KRI,
      dimension: DIMENSION.INTEREST_RATE_RISK,
      dimension2: null,
    };
  }, [selectedBusinessEntityID, selectedMonthEnd]);

  useEffect(() => {
    if (
      !appliedFilters?.selectedBusinessEntityID ||
      !appliedFilters?.selectedModelDatabase ||
      !appliedFilters?.selectedMonthEnd
    ) {
      setHrGapResultData([]);
      setApprovalFlow([]);
      return;
    }

    const currentSignature = JSON.stringify({
      selectedBusinessEntityID: appliedFilters.selectedBusinessEntityID,
      selectedModelDatabase: appliedFilters.selectedModelDatabase,
      selectedMonthEnd: appliedFilters.selectedMonthEnd?.format("YYYY-MM-DD"),
      advancedNII: appliedFilters.advancedNII,
      selectedFigureDivisor: appliedFilters.selectedFigureDivisor,
      selectedDecimalsIn: appliedFilters.selectedDecimalsIn,
    });

    if (lastSubmittedFiltersRef.current === currentSignature) {
      return;
    }

    lastSubmittedFiltersRef.current = currentSignature;

    fetchHrGapResultReport(UP100);

    fetchApprovalFlowData({
      ...APPROVAL_FLOW_PARAMS,
      businessEntityID: appliedFilters.selectedBusinessEntityID,
      selectedReportDate: appliedFilters.selectedMonthEnd,
    });
  }, [
    appliedFilters,
    fetchHrGapResultReport,
    fetchApprovalFlowData,
    setHrGapResultData,
    setApprovalFlow,
    APPROVAL_FLOW_PARAMS,
  ]);

  const lastSubmittedFiltersRef = useRef(null);
  const mappedFigureDivisors = useMemo(
    () => (figureDivisors || []).map((x) => ({ id: x.value, name: x.name })),
    [figureDivisors],
  );

  const mappedDecimalsIn = useMemo(
    () => (decimalsIn || []).map((x) => ({ id: x.value, name: x.name })),
    [decimalsIn],
  );

  const latestAnnualNIIForecastAmount = useMemo(() => {
    const targetKey = selectedMonthEnd?.format("MM/DD/YYYY");

    const latestAnnualNode = hrGapResultData?.find(
      (item) =>
        item?.reportingAccountName === LATEST_ANNUAL_NII_FORECAST_ACCOUNT_NAME,
    );

    const datas = latestAnnualNode?.datas || {};
    const keys = Object.keys(datas);

    if (!keys.length) return null;

    if (targetKey && Object.prototype.hasOwnProperty.call(datas, targetKey)) {
      return datas[targetKey];
    }

    const earliestKey = keys.sort(
      (a, b) =>
        dayjs(a, "MM/DD/YYYY").valueOf() - dayjs(b, "MM/DD/YYYY").valueOf(),
    )[0];

    return datas[earliestKey] ?? null;
  }, [hrGapResultData, selectedMonthEnd]);

  const preprocessedData = flattenReportData(
    hrGapResultData,
    selectedFigureDivisor,
    selectedDecimalsIn,
  );

  const handleSendApproverEmailNotify = () => {
    if (selectedModelDatabase && selectedBusinessEntityID) {
      sendApproverEmailNotifyForSelectedReport({
        ...APPROVAL_FLOW_PARAMS,
        isGlobal: null,
        isModelerReport: null,
        isTreasuryKRIReport: null,
        isKRISummaryReport: null,
        isSFCKRIGlobalSummaryReport: null,
        isRegionKRIGlobalSummaryReport: null,
        isTFSCKRIGlobalSummaryReport: null,
      });
    }
  };

  const handleStepApproval = ({
    currentStep,
    currentApprover,
    type,
    comment,
    reportDate,
  } = {}) => {
    return saveApprovalData({
      currentApprover,
      type,
      comment,
      reportDate,
    })
      .then(() => fetchApprovalFlowData({ ...APPROVAL_FLOW_PARAMS }))
      .then(() =>
        sendApproverEmailNotifyForSelectedReport({
          ...APPROVAL_FLOW_PARAMS,
          isGlobal: null,
          isModelerReport: null,
          isTreasuryKRIReport: null,
          isKRISummaryReport: null,
          isSFCKRIGlobalSummaryReport: null,
          isRegionKRIGlobalSummaryReport: null,
          isTFSCKRIGlobalSummaryReport: null,
        }),
      );
  };

  return (
    <>
      {!hrGapResultDataError &&
      niiResultTableData &&
      niiResultTableData?.length > 0 ? (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              pr: "10px",
            }}
          >
            {approvalFlow?.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleSendApproverEmailNotify}
                sx={{
                  fontSize: "0.8rem",
                  boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
                  height: "38px",
                  mt: "-10px",
                }}
                disabled={
                  selectedUserDetailLoading ||
                  hrFiltersDataLoading ||
                  hrGapResultDataLoading ||
                  !selectedBusinessEntityID ||
                  !selectedModelDatabase ||
                  sendEmailNotifyLoading
                }
              >
                Send Reminder Notification
              </Button>
            )}
          </Box>
          <ProgressSteps
            stepsData={approvalFlow}
            isGlobal={true}
            isModelerReport={true}
            isTreasuryKRIReport={false}
            isKRISummaryReport={false}
            isSFCKRIGlobalSummaryReport={false}
            isRegionKRIGlobalSummaryReport={false}
            isTFSCKRIGlobalSummaryReport={false}
            reportDate={selectedMonthEnd}
            loggedInEmail={email}
            handleStepApproval={handleStepApproval}
          />
        </>
      ) : (
        <Typography
          sx={{
            color: "#6a6a6a",
            marginBottom: 2,
            fontStyle: "italic",
          }}
        >
          Please generate the report to view the Approval Progress for NII
          Sensitivity Global Report
        </Typography>
      )}

      {localCurrency && (
        <Typography
          variant="body2"
          component="div"
          sx={{
            color: "#6a6a6a",
            fontWeight: "normal",
            fontStyle: "italic",
            marginTop: -1,
            marginBottom: 2,
          }}
        >
          (The below figures are shown in {localCurrency})                
        </Typography>
      )}
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <InlineDropdown
            label="Show Figures In"
            labelId="show-figures-in"
            options={mappedFigureDivisors}
            selectedValue={selectedFigureDivisor}
            handleChange={setSelectedFigureDivisor}
            placeholder={"Show Figures In"}
            dropdownWidth={150}
            isLoading={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              hrGapResultDataLoading
            }
          />
        </Grid>
        {localCurrency && (
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel id="currency-type">Currency</InputLabel>
            <Typography
              sx={{
                color: "#6a6a6a",
                fontWeight: "bold",
                marginTop: 1,
              }}
            >
              {localCurrency}
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={4}>
          <InlineDropdown
            label="Show Decimal In"
            labelId="show-decimal-in"
            options={mappedDecimalsIn}
            selectedValue={selectedDecimalsIn}
            handleChange={setSelectedDecimalsIn}
            placeholder={"Show Decimals In"}
            dropdownWidth={150}
            isLoading={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              hrGapResultDataLoading
            }
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-end",
          }}
        >
          <div className={classes.nIISensitivityModelerGlobalExportBtn}>
            <NIISensitivityModelerGlobalExport
              niiInputTablecolumn={niiInputTablecolumn}
              niiInputTableData={niiInputTableData}
              niiOutputTablecolumn={niiOutputTablecolumn}
              niiOutputTableData={niiOutputTableData}
              niiResultTableColumn={niiResultTableColumn}
              niiResultTableData={niiResultTableData}
              disabled={
                !niiInputTablecolumn ||
                !niiInputTablecolumn?.length ||
                !niiInputTableData ||
                !niiInputTableData?.length ||
                !preprocessedData ||
                !preprocessedData?.length ||
                hrGapResultDataLoading ||
                hrGapResultDataError
              }
            />
          </div>
        </Grid>

        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          {!hrGapResultDataLoading && hrGapResultDataError ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                There was an error in fetching the data. Please try again later.
              </Typography>
            </Item>
          ) : (!hrGapResultDataLoading && !hrGapResultData?.length) ||
            !preprocessedData?.length ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                No data available for the selected filters.
              </Typography>
            </Item>
          ) : hrGapResultDataLoading ? (
            <Skeleton
              variant="rectangular"
              width="100%"
              height={300}
              animation="pulse"
              style={{ marginBottom: 8 }}
            />
          ) : (
            <>
              {preprocessedData && preprocessedData?.length && (
                <>
                  <NiiInputTable
                    NIIData={preprocessedData}
                    setNiiInputTableData={setNiiInputTableData}
                    setNiiInputTablecolumn={setNiiInputTablecolumn}
                  />
                  <NiiOutputTable
                    niiInputTableData={niiInputTableData}
                    niiInputTablecolumn={niiInputTablecolumn}
                    setNiiOutputTablecolumn={setNiiOutputTablecolumn}
                    setNiiOutputTableData={setNiiOutputTableData}
                    selectedDecimalsIn={selectedDecimalsIn}
                    selectedFigureDivisor={selectedFigureDivisor}
                  />
                  <NiiResultTable
                    niiOutputTablecolumn={niiOutputTablecolumn}
                    niiOutputTableData={niiOutputTableData}
                    setNiiResultTableColumn={setNiiResultTableColumn}
                    setNiiResultTableData={setNiiResultTableData}
                    selectedDecimalsIn={selectedDecimalsIn}
                    selectedFigureDivisor={selectedFigureDivisor}
                    selectedMonthEnd={selectedMonthEnd}
                    latestAnnualNIIForecastAmount={
                      latestAnnualNIIForecastAmount
                    }
                    selectedBusinessEntityID={selectedBusinessEntityID}
                    selectedModelDatabase={selectedModelDatabase}
                    isWritableSfc={isWritableSfc}
                    isAdmin={isAdmin}
                    toSave={toSave}
                    toCompare={toCompare}
                  />
                </>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default NIISensitivityModelerGlobalReports;
